/**
 * Organization-provisioned setup — machine-wide org.json discovery.
 *
 * When an organization rolls this server out to many people, IT creates ONE
 * app registration and grants admin consent ONCE. Every subsequent user used
 * to be asked to paste that app's Client ID into the wizard by hand. Instead,
 * IT can now push a small `org.json` to a machine-wide location (Intune, GPO,
 * Jamf, or any file-drop) and setup picks the app up automatically — the
 * wizard goes straight to sign-in with nothing to type.
 *
 * Search order:
 *   1. PA_MCP_ORG_CONFIG            — explicit file path (highest precedence)
 *   2. Windows: %ProgramData%\powerautomate-mcp\org.json
 *      macOS:   /Library/Application Support/powerautomate-mcp/org.json
 *      Linux:   /etc/powerautomate-mcp/org.json
 *
 * These are machine-wide (admin-writable) locations by design — the file is
 * provisioned by IT, not by the user. Validation fails closed: anything
 * malformed is ignored with a warning and the wizard continues as if the
 * file weren't there.
 */

import { join } from "node:path";
import { existsSync, readFileSync, statSync } from "node:fs";
import { CONFIG_DIR_NAME } from "../utils/constants.js";
import { logger } from "../utils/logger.js";

const ORG_CONFIG_FILE_NAME = "org.json" as const;

/** Reject absurdly large files before parsing — org.json is a few hundred bytes. */
const MAX_ORG_CONFIG_BYTES = 64 * 1024;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface OrgConfig {
  clientId: string;
  tenantId: string | null;
  /**
   * Optional environment pin. When present, the MCP server can bootstrap
   * entirely from org.json (no per-user config file, no wizard) — the user's
   * first in-chat sign_in gets them running.
   */
  environmentId: string | null;
  environmentRegion: string | null;
  environmentName: string | null;
  dataverseUrl: string | null;
  /** Where the config was found — shown to the user so the source is auditable. */
  source: string;
}

/** Candidate paths in precedence order for the current platform. */
export function getOrgConfigSearchPaths(): string[] {
  const paths: string[] = [];

  const explicit = process.env["PA_MCP_ORG_CONFIG"];
  if (explicit && explicit.trim()) {
    paths.push(explicit.trim());
  }

  if (process.platform === "win32") {
    const programData = process.env["ProgramData"] || "C:\\ProgramData";
    paths.push(join(programData, CONFIG_DIR_NAME, ORG_CONFIG_FILE_NAME));
  } else if (process.platform === "darwin") {
    paths.push(join("/Library", "Application Support", CONFIG_DIR_NAME, ORG_CONFIG_FILE_NAME));
  } else {
    paths.push(join("/etc", CONFIG_DIR_NAME, ORG_CONFIG_FILE_NAME));
  }

  return paths;
}

/**
 * Read and validate the first org config found. Returns null when no file
 * exists or the first candidate found is unusable — never throws.
 */
export function readOrgConfig(): OrgConfig | null {
  for (const path of getOrgConfigSearchPaths()) {
    if (!existsSync(path)) continue;

    try {
      const size = statSync(path).size;
      if (size > MAX_ORG_CONFIG_BYTES) {
        logger.warn({ path, size }, "Organization config ignored: file too large");
        return null;
      }

      const parsed = JSON.parse(readFileSync(path, "utf-8")) as {
        clientId?: unknown;
        tenantId?: unknown;
        environmentId?: unknown;
        environmentRegion?: unknown;
        environmentName?: unknown;
        dataverseUrl?: unknown;
      };

      const clientId = typeof parsed.clientId === "string" ? parsed.clientId.trim() : "";
      if (!UUID_RE.test(clientId)) {
        logger.warn({ path }, "Organization config ignored: clientId is not a valid UUID");
        return null;
      }

      let tenantId: string | null = null;
      if (typeof parsed.tenantId === "string" && parsed.tenantId.trim()) {
        if (UUID_RE.test(parsed.tenantId.trim())) {
          tenantId = parsed.tenantId.trim();
        } else {
          logger.warn({ path }, "Organization config: tenantId is not a valid UUID — ignoring tenantId only");
        }
      }

      // Environment fields are free-form strings (BAP environment ids are not
      // always UUIDs — "Default-<guid>" is real); schema validation happens
      // when a bootstrap config is built from them.
      const str = (v: unknown): string | null =>
        typeof v === "string" && v.trim() ? v.trim() : null;

      return {
        clientId,
        tenantId,
        environmentId: str(parsed.environmentId),
        environmentRegion: str(parsed.environmentRegion),
        environmentName: str(parsed.environmentName),
        dataverseUrl: str(parsed.dataverseUrl),
        source: path,
      };
    } catch (err) {
      logger.warn(
        { path, err: err instanceof Error ? err.message : String(err) },
        "Organization config ignored: unreadable or malformed JSON"
      );
      return null;
    }
  }

  return null;
}

/**
 * Build a full server configuration from an org.json that pins an
 * environment. This is the zero-touch rollout path: IT pushes org.json (and
 * the AI app's MCP config), the server boots with no per-user config file,
 * and the user's first in-chat sign_in gets them running. Returns null when
 * there is no org.json or it has no environmentId — the caller falls through
 * to its normal missing-config behavior.
 *
 * Shape mirrors buildConfigFromEnv() in config/loader.ts; the result still
 * goes through validateConfig, so nothing skips schema validation.
 */
export function buildOrgBootstrapConfig(): { config: Record<string, unknown>; source: string } | null {
  const org = readOrgConfig();
  if (!org || !org.environmentId) {
    return null;
  }
  return {
    source: org.source,
    config: {
      auth: {
        clientId: org.clientId,
        tenantId: org.tenantId,
      },
      environments: {
        default: "main",
        main: {
          id: org.environmentId,
          region: org.environmentRegion ?? "unitedstates",
          displayName: org.environmentName ?? "Power Platform",
          ...(org.dataverseUrl ? { dataverseUrl: org.dataverseUrl } : {}),
        },
      },
    },
  };
}

/**
 * `--emit-org-config` — print an org.json for the current working setup.
 *
 * Run by whoever completed setup first (typically IT, right after creating
 * the app registration and granting consent). The JSON goes to stdout so it
 * composes (`powerautomate-mcp --emit-org-config > org.json`); the deploy
 * instructions go to stderr. Returns the process exit code.
 */
export function runEmitOrgConfig(getConfigPath: () => string): number {
  const configPath = getConfigPath();
  if (!existsSync(configPath)) {
    console.error("No configuration found — run `powerautomate-mcp --setup` first.");
    return 1;
  }

  let clientId: string | undefined;
  let tenantId: string | null;
  let environment: { id?: string; region?: string; displayName?: string; dataverseUrl?: string } | undefined;
  try {
    const parsed = JSON.parse(readFileSync(configPath, "utf-8")) as {
      auth?: { clientId?: string; tenantId?: string | null };
      environments?: { default?: string } & Record<string, unknown>;
    };
    clientId = parsed.auth?.clientId;
    tenantId = parsed.auth?.tenantId ?? null;
    const defaultName = parsed.environments?.default;
    if (defaultName && typeof parsed.environments?.[defaultName] === "object") {
      environment = parsed.environments[defaultName] as typeof environment;
    }
  } catch {
    console.error(`Could not read ${configPath} — is it valid JSON?`);
    return 1;
  }

  if (!clientId || !UUID_RE.test(clientId)) {
    console.error("The current configuration has no valid Client ID — run `powerautomate-mcp --setup` first.");
    return 1;
  }

  // Include the environment pin when the working setup has one — it's what
  // lets the MCP server on other machines boot straight from org.json with
  // no wizard at all.
  const orgJson = JSON.stringify(
    {
      clientId,
      tenantId,
      ...(environment?.id
        ? {
            environmentId: environment.id,
            ...(environment.region ? { environmentRegion: environment.region } : {}),
            ...(environment.displayName ? { environmentName: environment.displayName } : {}),
            ...(environment.dataverseUrl ? { dataverseUrl: environment.dataverseUrl } : {}),
          }
        : {}),
    },
    null,
    2
  );
  console.log(orgJson);

  const dest =
    process.platform === "win32"
      ? "%ProgramData%\\powerautomate-mcp\\org.json"
      : process.platform === "darwin"
        ? "/Library/Application Support/powerautomate-mcp/org.json"
        : "/etc/powerautomate-mcp/org.json";
  console.error("");
  console.error("Deploy this file to each machine at:");
  console.error(`  ${dest}`);
  console.error("(push it with Intune, GPO, Jamf, or any file distribution you already use)");
  console.error("");
  console.error("On those machines, `powerautomate-mcp --setup` will find the file and skip");
  console.error("straight to sign-in — nobody has to type the Client ID.");
  return 0;
}
